import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ModalComponent } from '../../components/ModalComponent'
import { PromptsManagementService } from '../../services'
import menuStyles from '../AIManagerPage/styles.module.css'
import styles from './styles.module.css'

const SUCCESS_TOAST_STYLE = {
  background: '#16a34a',
  color: '#ffffff',
}

const ERROR_TOAST_STYLE = {
  background: '#dc2626',
  color: '#ffffff',
}

const hasReplacementChar = (value) => value.includes('�')
const looksLikeMojibake = (value) => /Ã[\u0080-\u00BF]|â[\u0080-\u00BF]/.test(value)

const getTextQualityScore = (value) => {
  if (!value) return 0
  const mojibakeMatches = value.match(/Ã[\u0080-\u00BF]|â[\u0080-\u00BF]|�/g) || []
  const ptAccentMatches = value.match(/[áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ]/g) || []
  return ptAccentMatches.length - mojibakeMatches.length * 2
}

const normalizePromptEncoding = (value) => {
  if (!value || typeof value !== 'string') return ''
  if (hasReplacementChar(value)) return value
  if (!looksLikeMojibake(value)) return value

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0)))
    const decoded = new TextDecoder('utf-8').decode(bytes)
    if (!decoded) return value

    return getTextQualityScore(decoded) >= getTextQualityScore(value)
      ? decoded
      : value
  } catch {
    return value
  }
}

function PromptManagerEditPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams()
  const initialPrompt = location?.state?.promptItem || null

  const [promptData, setPromptData] = useState(initialPrompt)
  const [promptText, setPromptText] = useState(
    normalizePromptEncoding(initialPrompt?.prompt || ''),
  )
  const [schemaText, setSchemaText] = useState(
    initialPrompt?.schema ? JSON.stringify(initialPrompt.schema, null, 2) : '',
  )
  const [isLoading, setIsLoading] = useState(!initialPrompt)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [commits, setCommits] = useState([])
  const [selectedCommitId, setSelectedCommitId] = useState(
    initialPrompt?.promptId || '',
  )
  const [initialFormState, setInitialFormState] = useState({
    prompt: normalizePromptEncoding(initialPrompt?.prompt || ''),
    schema: initialPrompt?.schema ? JSON.stringify(initialPrompt.schema, null, 2) : '',
    selectedCommitId: initialPrompt?.promptId || '',
  })
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false)

  const hasSchema = useMemo(
    () => Boolean(promptData?.schema) || Boolean(schemaText.trim()),
    [promptData, schemaText],
  )
  const showSchemaButton = Boolean(promptData?.schema)
  const hasUnsavedChanges =
    promptText !== initialFormState.prompt ||
    schemaText !== initialFormState.schema ||
    selectedCommitId !== initialFormState.selectedCommitId

  useEffect(() => {
    const loadByList = async () => {
      if (initialPrompt) return

      setIsLoading(true)
      setError('')
      const response = await PromptsManagementService.getAllPrompts()

      if (response?.responseStatus === 200 && Array.isArray(response?.data)) {
        const selectedPrompt = response.data.find((item) => item.uuid === id)
        if (!selectedPrompt) {
          setError('Prompt não encontrado.')
          setIsLoading(false)
          return
        }

        setPromptData(selectedPrompt)
        const normalizedPrompt = normalizePromptEncoding(selectedPrompt.prompt || '')
        setPromptText(normalizedPrompt)
        const formattedSchema = selectedPrompt.schema
          ? JSON.stringify(selectedPrompt.schema, null, 2)
          : ''
        setSchemaText(formattedSchema)
        setInitialFormState({
          prompt: normalizedPrompt,
          schema: formattedSchema,
          selectedCommitId: selectedPrompt.promptId || '',
        })
        setSelectedCommitId(selectedPrompt.promptId || '')
      } else {
        setError('Não foi possível carregar o prompt selecionado.')
      }

      setIsLoading(false)
    }

    loadByList()
  }, [id, initialPrompt])

  useEffect(() => {
    const loadCommits = async () => {
      if (!promptData?.uuid) return

      const response = await PromptsManagementService.getPromptCommits(promptData.uuid)
      if (response?.responseStatus === 200 && Array.isArray(response?.data)) {
        setCommits(response.data)
      }
    }

    loadCommits()
  }, [promptData?.uuid])

  const handleSelectCommit = (commitId) => {
    setSelectedCommitId(commitId)
    const commit = commits.find((item) => item.uuid === commitId)
    if (!commit) return

    const normalizedPrompt = normalizePromptEncoding(commit.prompt || '')
    setPromptText(normalizedPrompt)
    setSchemaText(commit.schema ? JSON.stringify(commit.schema, null, 2) : '')
  }

  const handleSave = async () => {
    if (!promptData) return

    let parsedSchema = promptData.schema
    if (hasSchema) {
      try {
        parsedSchema = schemaText.trim() ? JSON.parse(schemaText) : undefined
        if (
          parsedSchema === null ||
          Array.isArray(parsedSchema) ||
          typeof parsedSchema !== 'object'
        ) {
          setError('O campo de schema precisa conter um JSON objeto válido.')
          return
        }
      } catch {
        setError('O campo de schema precisa conter um JSON válido.')
        return
      }
    }

    setIsSaving(true)
    setError('')

    const payload = {
      name: promptData.name,
      prompt: promptText,
      selectedCommitId,
      ...(hasSchema ? { schema: parsedSchema } : {}),
      ...(promptData.function ? { function: promptData.function } : {}),
      ...(promptData.path ? { path: promptData.path } : {}),
    }

    const response = await PromptsManagementService.updatePromptById(id, payload)
    setIsSaving(false)

    if (response?.responseStatus === 200 || response?.responseStatus === 204) {
      toast.success('Prompt salvo com sucesso.', {
        style: SUCCESS_TOAST_STYLE,
      })
      navigate('/PromptManagerList')
      return
    }

    const backendMessage =
      response?.response?.data?.details?.[0]?.message ||
      response?.response?.data?.message
    const errorMessage = backendMessage
      ? `Não foi possível salvar o prompt: ${backendMessage}`
      : 'Não foi possível salvar o prompt.'

    setError(errorMessage)
    toast.error(errorMessage, {
      style: ERROR_TOAST_STYLE,
    })
  }

  return (
    <div className={menuStyles.page}>
      <aside className={menuStyles.sideMenu}>
        <p className={menuStyles.menuTitle}>AI Manager</p>
        <Link
          to="/AIManager"
          className={menuStyles.menuLink}
        >
          Home Ai Manager
        </Link>
        <Link
          to="/PromptManagerList"
          className={`${menuStyles.menuLink} ${menuStyles.menuLinkActive}`}
        >
          Prompt Manager
        </Link>
        <Link
          to="/ChatSocket"
          className={menuStyles.menuLink}
        >
          Voltar ao sistema
        </Link>
      </aside>

      <section className={menuStyles.content}>
        <div className={menuStyles.breadcrumb}>
          <Link to="/AIManager" className={menuStyles.breadcrumbLink}>
            AI Manager
          </Link>
          <span className={menuStyles.breadcrumbSep}>/</span>
          <Link to="/PromptManagerList" className={menuStyles.breadcrumbLink}>
            Prompt Manager
          </Link>
          <span className={menuStyles.breadcrumbSep}>/</span>
          <span className={menuStyles.breadcrumbCurrent}>Editar</span>
        </div>
        <div className={styles.header}>
          <h2 className={styles.title}>Edit Prompt</h2>
        </div>

        {isLoading ? (
          <p className={styles.infoText}>Carregando prompt...</p>
        ) : (
          <div className={styles.form}>
            <label className={styles.label} htmlFor="prompt-name">
              Nome
            </label>
            <input
              id="prompt-name"
              className={styles.input}
              value={promptData?.name || ''}
              readOnly
            />

            <label className={styles.label} htmlFor="prompt-commit">
              Commit
            </label>
            <select
              id="prompt-commit"
              className={styles.input}
              value={selectedCommitId}
              onChange={(event) => handleSelectCommit(event.target.value)}
            >
              {commits.map((commit) => (
                <option key={commit.uuid} value={commit.uuid}>
                  {new Date(commit.createdAt).toLocaleString('pt-BR')}
                </option>
              ))}
            </select>

            <label className={styles.label} htmlFor="prompt-textarea">
              Prompt
            </label>
            <textarea
              id="prompt-textarea"
              className={styles.textarea}
              value={promptText}
              onChange={(event) => setPromptText(event.target.value)}
              rows={14}
            />

            {/* {hasSchema && (
              <>
                <label className={styles.label} htmlFor="schema-textarea">
                  Schema (JSON)
                </label>
                <textarea
                  id="schema-textarea"
                  className={styles.schemaTextarea}
                  value={schemaText}
                  onChange={(event) => setSchemaText(event.target.value)}
                  rows={16}
                />
              </>
            )} */}

            {error ? <p className={styles.errorText}>{error}</p> : null}

            <div className={styles.actions}>
              {showSchemaButton ? (
                <button
                  className={styles.secondaryButton}
                  type="button"
                  onClick={() => setIsSchemaModalOpen(true)}
                >
                  Ver schema
                </button>
              ) : (
                <span />
              )}
              <div className={styles.actionsRight}>
                <button
                  className={styles.secondaryButton}
                  type="button"
                  onClick={() => navigate('/PromptManagerList')}
                >
                  Voltar
                </button>
                <button
                  className={styles.primaryButton}
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ModalComponent
          isOpen={isSchemaModalOpen}
          onClose={() => setIsSchemaModalOpen(false)}
          className={styles.schemaModal}
        >
          <div className={styles.schemaModalBody}>
            <h3 className={styles.schemaModalTitle}>Schema</h3>
            <pre className={styles.schemaModalContent}>{schemaText}</pre>
          </div>
        </ModalComponent>
      </section>
    </div>
  )
}

export default PromptManagerEditPage
